import React from "react";
import MetaTags from "../src/MetaTags";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import LayoutFirebase from "../src/LayoutFirebase";

const Index = () => {
  const { t } = useTranslation();

  return (
    <>
      <MetaTags
        title="Terms of Use"
        description="Learn about how we collect, use, and protect your data."
      />
      <LayoutFirebase>
        <div className="max-w-4xl mx-auto">
          <h1 className="sm:text-5xl text-4xl text-center font-medium my-8 text-gray-700">
            Terms of Use
          </h1>
          <div className="my-12 mx-4 prose lg:prose-xl">
            <p>Last updated: May 16th, 2024</p>

            <p>
              Please read these Terms of Use ("Terms", "Terms of Use") carefully
              before using the MoodBoardly (
              <a href="https://moodboardai.com/">https://moodboardai.com/</a>)
              and MyRoomDesigner.ai (
              <a href="https://myroomdesigner.ai/">
                https://myroomdesigner.ai/
              </a>
              ) websites (the "Service") operated by Mr Bojli Kft ("us", "we",
              or "our"). MoodBoardly is a visual communications platform that
              empowers people to create mood boards and inspirational pictures,
              while MyRoomDesigner.ai allows users to design and visualize room
              layouts.
            </p>

            <p>
              Your access to and use of the Service is conditioned on your
              acceptance of and compliance with these Terms. These Terms apply
              to all visitors, users, and others who access or use the Service.
              By accessing or using the Service, you agree to be bound by these
              Terms. If you disagree with any part of the terms, then you may
              not access the Service.
            </p>

            <h3>Accounts</h3>

            <p>
              When you create an account with us, you must provide information
              that is accurate, complete, and current at all times. Failure to
              do so constitutes a breach of the Terms, which may result in
              immediate termination of your account on our Service. You are
              responsible for safeguarding the password that you use to access
              the Service and for any activities or actions under your password,
              whether your password is with our Service or a third-party
              service. You agree not to disclose your password to any third
              party. You must notify us immediately upon becoming aware of any
              breach of security or unauthorized use of your account.
            </p>

            <h3>Intellectual Property</h3>

            <p>
              The Service and its original content, features, and functionality
              are and will remain the exclusive property of Mr Bojli Kft and its
              licensors. The Service is protected worldwide by copyright,
              trademark, and other laws. Our trademarks and trade dress may not
              be used in connection with any product or service without the
              prior written consent of Mr Bojli Kft.
            </p>

            <h3>Data Privacy</h3>

            <p>
              MoodBoardly’s and MyRoomDesigner.ai’s Privacy Policies describe
              how these services collect, use, transfer, disclose, and store
              your personal data. By creating an account with MoodBoardly or
              MyRoomDesigner.ai, you confirm that you have read, understood, and
              agree to our Privacy Policies.
            </p>

            <h3>Links To Other Websites</h3>

            <p>
              Our Service may contain links to third-party websites or services
              that are not owned or controlled by Mr Bojli Kft. Mr Bojli Kft has
              no control over, and assumes no responsibility for, the content,
              privacy policies, or practices of any third-party websites or
              services. You further acknowledge and agree that Mr Bojli Kft
              shall not be responsible or liable, directly or indirectly, for
              any damage or loss caused or alleged to be caused by or in
              connection with the use of or reliance on any such content, goods,
              or services available on or through any such websites or services.
              We strongly advise you to read the terms and conditions and
              privacy policies of any third-party websites or services that you
              visit.
            </p>

            <h3>Content and Designs</h3>

            <p>
              You represent and warrant that you own all rights, title, and
              interest in and to your User Content or that you have otherwise
              secured all necessary rights in your User Content as may be
              necessary to permit the access, use, and distribution thereof as
              contemplated by these Terms. As between you and MoodBoardly or
              MyRoomDesigner.ai, you own all right, title, and interest in and
              to your User Content. You grant MoodBoardly and MyRoomDesigner.ai
              a royalty-free and sublicensable license to display, host, copy,
              store, and use your User Content solely to the extent necessary to
              provide the Service to you. To the extent you include User Content
              in a Design that you’ve shared with others, you grant MoodBoardly
              and MyRoomDesigner.ai a perpetual, royalty-free, sublicensable
              license to display, host, copy, store, and use your User Content
              to the extent necessary to continue to make that Design available.
              When you upload content to MoodBoardly or MyRoomDesigner.ai,
              you’re guaranteeing that you have the rights to it. We never
              obtain any ownership over your content, but we do need you to give
              us certain rights to store it and have it ready for you to use in
              your designs.
            </p>

            <h3>Subscription and Renewals</h3>

            <p>
              If you are subscribing to MoodBoardly or MyRoomDesigner.ai, you
              can sign up for either a monthly or annual subscription. Your
              subscription will automatically renew on a monthly or annual basis
              as applicable. You can cancel your subscription at any time by
              contacting our support team or through your account settings. If
              you cancel your subscription, you will not receive a refund or
              credit for any amounts that have already been billed. Subscription
              fees are non-refundable, except where required by law.
            </p>

            <h3>Refund Policy</h3>

            <p>
              All purchases made on the Service are final and non-refundable. We
              do not offer refunds or credits for any partially used
              subscription periods. However, if you experience any issues with
              the Service or believe you are entitled to a refund, please
              contact our support team at{" "}
              <a href="mailto:support@moodboardly.com">
                support@moodboardly.com
              </a>
              , and we will review your request on a case-by-case basis.
            </p>

            <h3>Termination</h3>

            <p>
              We may terminate or suspend access to our Service immediately,
              without prior notice or liability, for any reason whatsoever,
              including without limitation if you breach the Terms. All
              provisions of the Terms which by their nature should survive
              termination shall survive termination, including, without
              limitation, ownership provisions, warranty disclaimers, indemnity,
              and limitations of liability.
            </p>

            <h3>Governing Law</h3>

            <p>
              These Terms shall be governed and construed in accordance with the
              laws of Hungary, without regard to its conflict of law provisions.
              Our failure to enforce any right or provision of these Terms will
              not be considered a waiver of those rights. If any provision of
              these Terms is held to be invalid or unenforceable by a court, the
              remaining provisions of these Terms will remain in effect. These
              Terms constitute the entire agreement between us regarding our
              Service, and supersede and replace any prior agreements we might
              have between us regarding the Service.
            </p>

            <h3>Changes</h3>

            <p>
              We reserve the right, at our sole discretion, to modify or replace
              these Terms at any time. If a revision is material, we will try to
              provide at least 30 days' notice prior to any new terms taking
              effect. What constitutes a material change will be determined at
              our sole discretion. By continuing to access or use our Service
              after those revisions become effective, you agree to be bound by
              the revised terms. If you do not agree to the new terms, please
              stop using the Service.
            </p>

            <h3>Contact Us</h3>

            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <ul>
              <li>
                By email:{" "}
                <a href="mailto:support@moodboardly.com">
                  support@moodboardly.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </LayoutFirebase>
    </>
  )
};
export default Index;

export const getStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
    revalidate: 1,
  };
};
